// Timeline lateral compacto dos eventos do Agno.
// Mostra reasoning, tool calls, delegations, métricas — tudo que NÃO é texto da resposta.

import { useState } from 'preact/hooks';
import { formatTokens, formatDuration } from '../lib/format';
import type { AnyAgnoEvent } from '../types/agno-events';

interface Props {
  events: AnyAgnoEvent[];
}

export function EventTimeline({ events }: Props) {
  if (events.length === 0) return null;

  return (
    <div class="space-y-1.5">
      {events.map((e, i) => (
        <EventRow key={i} event={e} />
      ))}
    </div>
  );
}

function EventRow({ event }: { event: AnyAgnoEvent }) {
  const [open, setOpen] = useState(false);
  const name = event.event;

  const render = () => {
    // Reasoning
    if (name === 'ReasoningStarted') return <Pill icon="ph-brain" text="Raciocínio iniciado" tone="muted" />;
    if (name === 'ReasoningCompleted') return <Pill icon="ph-brain" text="Raciocínio concluído" tone="muted" />;
    if (name === 'ReasoningStep') {
      const e = event as { content?: string; reasoning_content?: string };
      const txt = e.reasoning_content || e.content || '';
      return <ExpandableCard icon="ph-brain" title="Reasoning step" body={txt} open={open} setOpen={setOpen} />;
    }

    // Tool calls
    if (name === 'ToolCallStarted') {
      const e = event as { tool?: { tool_name?: string; tool_args?: Record<string, unknown> } };
      const args = e.tool?.tool_args;
      return (
        <ExpandableCard
          icon="ph-wrench"
          title={`Chamando tool · ${e.tool?.tool_name ?? '?'}`}
          body={args ? JSON.stringify(args, null, 2) : ''}
          open={open}
          setOpen={setOpen}
        />
      );
    }
    if (name === 'ToolCallCompleted') {
      const e = event as { tool?: { tool_name?: string; result?: unknown; metrics?: { time?: number } } };
      const t = e.tool?.metrics?.time;
      return (
        <ExpandableCard
          icon="ph-check-circle"
          title={`Tool ok · ${e.tool?.tool_name ?? '?'}${t ? ` · ${formatDuration(t)}` : ''}`}
          body={e.tool?.result != null ? JSON.stringify(e.tool.result, null, 2) : '(sem retorno)'}
          tone="success"
          open={open}
          setOpen={setOpen}
        />
      );
    }
    if (name === 'ToolCallError') {
      const e = event as { tool?: { tool_name?: string; error?: string } };
      return (
        <Pill icon="ph-x-circle" text={`Erro · ${e.tool?.tool_name ?? '?'}`} tone="destructive" />
      );
    }

    // Delegation (Tasks)
    if (name === 'TaskCreated') {
      const e = event as { task?: { description?: string; member?: string } };
      return (
        <Pill icon="ph-paper-plane-tilt" text={`Delegado a ${e.task?.member ?? '?'}${e.task?.description ? ' · ' + e.task.description.slice(0, 60) : ''}`} tone="primary" />
      );
    }
    if (name === 'TaskIterationStarted') {
      const e = event as { member?: string; iteration?: number };
      return <Pill icon="ph-caret-right" text={`${e.member ?? 'Membro'} · iteração ${e.iteration ?? '?'}`} tone="muted" />;
    }
    if (name === 'TaskIterationCompleted') {
      const e = event as { member?: string };
      return <Pill icon="ph-check-circle" text={`${e.member ?? 'Membro'} concluído`} tone="success" />;
    }

    // Model
    if (name === 'ModelRequestStarted') {
      const e = event as { model?: string };
      return <Pill icon="ph-cpu" text={`Modelo · ${e.model ?? '?'}`} tone="muted" />;
    }
    if (name === 'ModelRequestCompleted') {
      const e = event as {
        input_tokens?: number;
        output_tokens?: number;
        time_to_first_token?: number;
      };
      const parts = [
        e.input_tokens != null ? `in ${formatTokens(e.input_tokens)}` : null,
        e.output_tokens != null ? `out ${formatTokens(e.output_tokens)}` : null,
        e.time_to_first_token != null ? `ttft ${formatDuration(e.time_to_first_token)}` : null,
      ].filter(Boolean);
      return <Pill icon="ph-lightning" text={parts.join(' · ')} tone="muted" />;
    }

    // Memory
    if (name === 'MemoryUpdateStarted') return <Pill icon="ph-database" text="Atualizando memória" tone="muted" />;
    if (name === 'MemoryUpdateCompleted') return <Pill icon="ph-database" text="Memória atualizada" tone="success" />;

    // Session
    if (name === 'SessionSummaryCompleted') return <Pill icon="ph-sparkle" text="Resumo da sessão gerado" tone="muted" />;

    // Lifecycle
    if (name === 'TeamRunStarted' || name === 'RunStarted') return <Pill icon="ph-paper-plane-tilt" text="Run iniciado" tone="muted" />;
    if (name === 'TeamRunPaused') return <Pill icon="ph-pause-circle" text="Run pausado" tone="warning" />;
    if (name === 'TeamRunError') return <Pill icon="ph-x-circle" text="Erro no run" tone="destructive" />;
    if (name === 'TeamRunCancelled') return <Pill icon="ph-x-circle" text="Run cancelado" tone="muted" />;

    // Outros eventos: pílula neutra
    return <Pill icon="ph-caret-right" text={name} tone="muted" />;
  };

  return <div class="flex">{render()}</div>;
}

function Pill({
  icon,
  text,
  tone,
}: {
  icon: string;
  text: string;
  tone: 'muted' | 'success' | 'destructive' | 'warning' | 'primary';
}) {
  const cls = {
    muted: 'text-muted-foreground border-border',
    success: 'text-success border-border',
    destructive: 'text-destructive border-border',
    warning: 'text-warning border-border',
    primary: 'text-foreground border-border bg-muted',
  }[tone];
  return (
    <div class={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border text-xs font-medium ${cls}`}>
      <i class={`ph ${icon} text-sm`} />
      <span class="font-mono">{text}</span>
    </div>
  );
}

function ExpandableCard({
  icon,
  title,
  body,
  tone = 'muted',
  open,
  setOpen,
}: {
  icon: string;
  title: string;
  body: string;
  tone?: 'muted' | 'success' | 'destructive';
  open: boolean;
  setOpen: (v: boolean) => void;
}) {
  const accent = {
    muted: 'text-muted-foreground',
    success: 'text-success',
    destructive: 'text-destructive',
  }[tone];

  return (
    <div class="border border-border rounded-md bg-card text-card-foreground shadow-xs overflow-hidden">
      <button
        class={`w-full flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium ${accent} hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50`}
        onClick={() => setOpen(!open)}
      >
        <i class={`ph ${open ? 'ph-caret-down' : 'ph-caret-right'} text-sm`} />
        <i class={`ph ${icon} text-sm`} />
        <span class="font-mono">{title}</span>
      </button>
      {open && body && (
        <pre class="text-[11px] font-mono p-2 border-t border-border bg-muted/30 whitespace-pre-wrap break-all max-h-60 overflow-auto">{body}</pre>
      )}
    </div>
  );
}
